import React from 'react';
import * as messages from '@radnuc/messages';

import { Map, Marker, TileLayer, GeoJSON, Popup } from 'react-leaflet'
import L from 'leaflet'
import * as geojson from 'geojson';
import { t } from 'i18next';
import './i18n';
import { uuidToString } from './UUID';
import { CoralInterface} from './ContextDisplay'

import './MapDisplay.css'

import pin_industrial_logo from './img/pin-industrial.png'
import pin_medical_logo from './img/pin-medical.png'
import pin_norm_logo from './img/pin-norm.png'
import pin_nuclear_logo from './img/pin-nuclear.png'
import pin_chemical_logo from './img/pin-chemical.png'
import pin_biological_logo from './img/pin-biological.png'
import pin_generic_logo from './img/pin-generic-white-32.png'
import pin_generic_gray_logo from './img/pin-generic-gray.png'
import active_badge_logo from './img/active-investigation-badge.png'
import pin_shadown_logo from './img/marker-shadow.png'
import location_circle_logo from './img/ic_my_location_48px.png'
import location_circle_white_logo from './img/ic_my_location_48px_white.png'
import location_vector_logo from './img/ic_navigation_48px-32.png'
import location_vector_white_logo from './img/ic_navigation_48px-32_white.png'
import recenter_logo from './img/gps-navi-arrow-circle-32_white.png'
import recenter_on_alert_logo from './img/warning-lock-48_white.png'
import recenter_disabled_logo from './img/unlock-48_white.png'
import map_day_logo from './img/sun-day-bright-light-48_white.png'
import map_night_logo from './img/weather-night-48_white.png'

type Investigation = messages.Encounter.Encounter | messages.Rad.Investigation;

interface InvestigationInterface {
    select: (key: Investigation) => void,
    unselect: () => void,
}

export interface MapDisplayProps {
    investigations: Investigation[],
    selectedInvestigation?: Investigation,
    investigationInterface: InvestigationInterface,

    positions: messages.Core.GPSReading[],
    radiationData: messages.Rad.RadiationReading[],

    liveStream: boolean,
    replayTime?: Date,

    coralInterface?: CoralInterface,
}

export interface MapDisplayState {
    keep_vehicle_centered: boolean,
    recenter_on_alert: boolean,
    map_mode_day: boolean,
    map_mode_day_simple: boolean,
    plot_since: number,
    zoom_level: number,
    inv_selected: boolean
}

export class MapDisplay extends React.Component<MapDisplayProps, MapDisplayState> {
    //programmatic_map_control: boolean = false;
    map_element: React.RefObject<any>;
    last_location_message: messages.Core.GPSReading | undefined;
    initialized: boolean = false;

    constructor(props: MapDisplayProps) {
        super(props)

        this.state = {
            keep_vehicle_centered: true,
            recenter_on_alert: true,
            map_mode_day: true,
            map_mode_day_simple: false,
            plot_since: 0,
            zoom_level: 17,
            inv_selected: false
        }

        this.map_element = React.createRef<any>();

        this.handleUnselectButton = this.handleUnselectButton.bind(this);
        this.handleRecenterButtonClick = this.handleRecenterButtonClick.bind(this);
        this.handleRecenterOnAlertButtonClick = this.handleRecenterOnAlertButtonClick.bind(this);
        this.handleMapModeButtonClick = this.handleMapModeButtonClick.bind(this);
        this.handleDayMapTypeClick = this.handleDayMapTypeClick.bind(this);
        this.handleClearTrailClick = this.handleClearTrailClick.bind(this);
    }

    componentDidMount() {
        this.patchMapTileGapBug();
        this.initialized = true;
    }

    componentDidUpdate(prevProps: MapDisplayProps) {
        //On New Investigation
        if (this.map_element.current !== null && this.props.investigations.length > prevProps.investigations.length) {
            if(this.state.recenter_on_alert){
                this.centerOnAlert();
            }
        }
        //Check for Unselection of CORAL source
        if(this.props.coralInterface !== undefined && prevProps.coralInterface !== undefined){
            if(prevProps.coralInterface.selectedSource !== undefined) {
                if(this.props.coralInterface.selectedSource !== undefined){
                    //Change in replay investigation -> reset coral selected Source
                    if(this.props.coralInterface.replayInvestigation?.id !== prevProps.coralInterface.replayInvestigation?.id){
                        this.props.coralInterface.updateSelectedSource({});
                    }

                    //Check for deselection of coral pin -> recenter to current replayed investigation
                    if(Object.keys(this.props.coralInterface.selectedSource).length === 0 && Object.keys(prevProps.coralInterface.selectedSource).length >0){
                        if(!this.props.liveStream && this.props.selectedInvestigation !== undefined){
                            if(this.props.selectedInvestigation.vehicle_location !== null)
                                this.map_element.current.leafletElement.setView([this.props.selectedInvestigation.vehicle_location.latitude,
                                                                                this.props.selectedInvestigation.vehicle_location.longitude])
                        }
                    }
                } else {
                    //Check for deselection and reset view to location of Chem investigation
                    if(!this.props.liveStream && this.props.selectedInvestigation !== undefined){
                        if(this.props.selectedInvestigation.vehicle_location !== null)
                            this.map_element.current.leafletElement.setView([this.props.selectedInvestigation.vehicle_location.latitude,
                                                                            this.props.selectedInvestigation.vehicle_location.longitude])
                    }
                }
            }
        }
    }
    centerOnAlert(){
        this.setState(() => ({
            keep_vehicle_centered: true
        }));
        //this.props.investigationInterface.unselect();
    }

    enableRecenterButton() {
        if (this.last_location_message !== undefined) {
            this.setState(() => ({
                keep_vehicle_centered: false
            }));
        }
    }

    getZoom(event: L.LayersControlEvent) {
        this.setState(() => ({
            zoom_level: event.target.getZoom(),
        }));
    }

    handleUnselectButton(event: React.MouseEvent<HTMLElement>) {
        this.props.investigationInterface.unselect();
        this.setState(() => ({
            keep_vehicle_centered: true
        }));
    }

    handleRecenterButtonClick(event: React.MouseEvent<HTMLElement>) {
        this.props.investigationInterface.unselect();
        this.setState(() => ({
            keep_vehicle_centered: true
        }));
    }

    handleRecenterOnAlertButtonClick(event: React.MouseEvent<HTMLElement>) {
        this.setState(() => ({
            recenter_on_alert: !this.state.recenter_on_alert
        }));
    }

    handleMapModeButtonClick(event: React.MouseEvent<HTMLElement>) {
        this.setState(() => ({
            map_mode_day: !this.state.map_mode_day
        }));
    }

    handleDayMapTypeClick(isSimple: boolean){
        this.setState(() => ({
            map_mode_day_simple: isSimple
        }));
    }

    handleClearTrailClick(){
        if(this.props.positions.length > 1){
            this.setState(() => ({
                plot_since: this.props.positions[this.props.positions.length-2].time
            }));
        } else if (this.props.positions.length === 1){
            this.setState(() => ({
                plot_since: this.props.positions[this.props.positions.length-1].time
            }));
        }
    }
    patchMapTileGapBug() {
        // Workaround for 1px lines appearing in some browsers due to fractional transforms
        // and resulting anti-aliasing. adapted from @cmulders' solution:
        // https://github.com/Leaflet/Leaflet/issues/3575#issuecomment-150544739
        let originalInitTile = (L.GridLayer.prototype as any)._initTile;
        if (originalInitTile.isPatched) return;
        L.GridLayer.include({
            _initTile: function (tile: any) {
                originalInitTile.call(this, tile);

                var tileSize = this.getTileSize();

                tile.style.width = tileSize.x + 1 + 'px';
                tile.style.height = tileSize.y + 1 + 'px';
            }
        });
        (L.GridLayer.prototype as any)._initTile.isPatched = true;
    }

    findPositionAtTime(this_time:Date){
        let best_position = this.last_location_message;
        const this_timestamp = this_time.getTime();
        if (this.props.positions.length > 0) {
            let min_pos_time_diff = 100000;//new Date(this.props.positions[this.props.positions.length-1].time).getTime() - this_timestamp;
            this.props.positions.forEach(position => {
                const this_time_diff = new Date(position.time).getTime() - this_timestamp;
                if(this_time_diff < min_pos_time_diff && this_time_diff > 0){
                    best_position = position;
                    min_pos_time_diff = this_time_diff
                }
            });
        }
        return best_position;
    }

    render() {
        let keep_vehicle_centered = this.state.keep_vehicle_centered;
        let position: [number, number] | undefined;

        //var current_location_message;
        if (this.props.positions.length > 0) {
            position = [this.props.positions[this.props.positions.length - 1].lat, this.props.positions[this.props.positions.length - 1].lon];
            this.last_location_message = this.props.positions[this.props.positions.length - 1];
        }

        if (this.props.selectedInvestigation) {
            if (this.props.selectedInvestigation.vehicle_location !== null) {
                let loc = this.props.selectedInvestigation.vehicle_location;
                position = [loc.latitude, loc.longitude];
            }
        }

        let map_center: [number, number] | undefined;
        if (this.state.keep_vehicle_centered || this.props.selectedInvestigation) {
            map_center = position;
        }
        //If map uninitialized, set mapt center to 0,0
        if (map_center === undefined && !this.initialized) {
            map_center = [0, 0];
        }

        let zoom_behavior: boolean | "center" = true;
        if (keep_vehicle_centered) {
            zoom_behavior = "center";
        }

        let unselectButton;
        if (this.props.selectedInvestigation) {
            unselectButton =
                <div className="round-button" style={{ position: 'absolute', right: '100px', top: '2px', padding: '5px' }} onClick={this.handleUnselectButton}>
                    <img src={pin_generic_logo} alt="Marker"/> <div style={{ width: '5px' }}> </div>{t('UNSELECT',{ns:'trans'})}
                </div>
        }

        let recenterButton;
        if ((!this.state.keep_vehicle_centered && this.last_location_message !== undefined) || this.props.selectedInvestigation) {
            recenterButton =
                <div className="round-button" style={{ position: 'absolute', left: '0px', bottom: '2px', padding: '5px' }} onClick={this.handleRecenterButtonClick}>
                    <img src={recenter_logo} alt="Recenter" /> <div style={{ width: '5px' }}> </div>{t('RE-CENTER',{ns:'trans'})}
                </div>
        }

        //Recenter on Alert button
        let recenter_status_img;
        if (this.state.recenter_on_alert) {
            recenter_status_img = recenter_on_alert_logo;
        } else {
            recenter_status_img = recenter_disabled_logo;
        }
        const recenterOnAlertButton =
            <div className="round-button" style={{ padding: '5px' }} onClick={this.handleRecenterOnAlertButtonClick}>
                <img src={recenter_status_img} alt="Recenter" />
            </div>


        //MapMode Button
        let map_mode_img;
        let map_url;
        let map_attribution;
        if (this.state.map_mode_day) {
            if(this.state.map_mode_day_simple){
                map_mode_img = map_day_logo;
                map_url = 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png';
                map_attribution = 'Map tiles by Carto, under CC BY 3.0. Data by OpenStreetMap, under ODbL';
            } else {
                map_mode_img = map_day_logo;
                map_url = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                map_attribution = "&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
            }
        } else {
            map_mode_img = map_night_logo;
            map_url = "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png";
            map_attribution = "Map tiles by Carto, under CC BY 3.0. Data by OpenStreetMap, under ODbL";
        }

        const mapModeButton =
            <div className="round-button" style={{ padding: '5px' }} onClick={this.handleMapModeButtonClick}>
                <img src={map_mode_img} alt="Day/Night Toggle" />
            </div>
        let background_color;
        if (this.state.map_mode_day) {
            background_color = 'white';
        } else {
            background_color = 'black';
        }

        const current_location = <PositionMarkerDisplay location={(this.props.replayTime !== undefined && !this.props.liveStream) ? this.findPositionAtTime(this.props.replayTime) : this.last_location_message} mapModeDay={this.state.map_mode_day} />

        let coralSourceMarkerDisplay;
        if(!this.props.liveStream){
            //if(this.props.coralInterface?.replayInvestigation?.id !== undefined && this.props.selectedInvestigation?.id !== undefined &&
            //   uuidToString(this.props.coralInterface.replayInvestigation.id) === uuidToString(this.props.selectedInvestigation.id)) {
            if(this.props.coralInterface?.replayInvestigation?.id !== undefined){
                coralSourceMarkerDisplay =
                    <MemoizedCoralSourceMarkerDisplay
                        selectedInvestigation={this.props.selectedInvestigation}
                        coralInterface={this.props.coralInterface}
                    />
            }
        }

        return (
            <>
                <div style={{ display: 'flex', position: 'relative', justifyContent: 'flexEnd' }}>
                    {unselectButton}
                </div>
                <Map center={map_center} zoom={this.state.zoom_level} animate={true} onDragStart={this.enableRecenterButton.bind(this)} tap={true} drag={true}
                    onZoomEnd={this.getZoom.bind(this)} maxZoom={19} touchZoom={zoom_behavior} scrollWheelZoom={zoom_behavior} ref={this.map_element} style={{ backgroundColor: background_color }}>
                    <TileLayer
                        url={map_url}
                        attribution={map_attribution}
                    />
                    <MemoizedInvestigationMarkerDisplay
                        investigations={this.props.investigations}
                        selectedInvestigation={this.props.selectedInvestigation}
                        investigationInterface={this.props.investigationInterface}
                    />
                    {coralSourceMarkerDisplay}
                    <MemoizedDoseTrailDisplay positions={this.props.positions} radiationData={this.props.radiationData} plotSince={this.state.plot_since}/>
                    {current_location}

                </Map>
                <div style={{ display: 'flex', position: 'relative', justifyContent: 'spaceBetween', bottom: '15px' }}>
                    {recenterButton}
                </div>
                <div style={{ display: 'flex', position: 'relative', justifyContent: 'flexEnd', bottom: '15px', minWidth:'300px'}}>
                    <div style={{ display: 'flex', position: 'absolute', right: '0px', bottom: '2px', padding: '5px' }}>
                        {mapModeButton}
                        {recenterOnAlertButton}
                    </div>
                </div>
                {/*HIDDEN DIVS FOR EXPERT SETTINGS*/}
                <div className={"map-select-day-map-simple-hidden"} style={{display:'none'}} onClick={()=>{this.handleDayMapTypeClick(true)}}/>
                <div className={"map-select-day-map-detailed-hidden"} style={{display:'none'}} onClick={()=>{this.handleDayMapTypeClick(false)}}/>
                <div className={"map-clear-trail-hidden"} style={{display:'none'}} onClick={()=>{this.handleClearTrailClick()}}/>
            </>
        )
    }
}
export default MapDisplay

/******************************************************************************/

export interface PositionMarkerDisplayProps {
    location: messages.Core.GPSReading | undefined,
    mapModeDay: boolean
}

export class PositionMarkerDisplay extends React.Component<PositionMarkerDisplayProps> {

    isValidHeading(heading:number){
        if(heading >=0 && heading < 360){
            return true;
        } else {
            return false;
        }
    }
    render() {
        var position_marker = null;
        if (this.props.location !== undefined && this.props.location !== null) {
            var position_icon;
            //console.log("heading: "+this.props.location.heading+" speed: "+this.props.location.mph);
            if (this.props.location.heading !== null && this.isValidHeading(this.props.location.heading) &&
                this.props.location.mph !== null && this.props.location.mph > 2) {
                var logo;
                if (this.props.mapModeDay) {
                    logo = location_vector_logo;
                } else {
                    logo = location_vector_white_logo;
                }
                position_icon = L.divIcon({
                    className: 'leaflet-div-icon-clear',
                    html:
                        '<div id="map_vehicle_icon" style="transform: rotate(' + this.props.location.heading + 'deg); opacity:0.6;"><img src="' + logo + '"></div>',
                    iconSize: [32, 32],
                    iconAnchor: [17, 17],
                });
            } else {
                var logo;
                if (this.props.mapModeDay) {
                    logo = location_circle_logo;
                } else {
                    logo = location_circle_white_logo;
                }
                position_icon = L.divIcon({
                    className: 'leaflet-div-icon-clear',
                    html:
                        '<div id="map_vehicle_icon" style="opacity:0.6;"><img src="' + logo + '"></div>',
                    iconSize: [32, 32],
                    iconAnchor: [17, 17],
                });
            }

            const key = this.props.location.id.mostSignificantBits.toLocaleString() +
                this.props.location.id.leastSignificantBits.toLocaleString();

            const location_lat = this.props.location.lat!==null ? this.props.location.lat : 0;
            const location_lon = this.props.location.lon!==null ? this.props.location.lon : 0;
            position_marker =
                <Marker position={[location_lat, location_lon]}
                    icon={position_icon}
                    data={key}
                    key={key}>
                </Marker>
        }
        return (
            position_marker
        )
    }
}

/******************************************************************************/

export interface InvestigationMarkerDisplayProps {
    investigations: Investigation[],
    selectedInvestigation?: Investigation,
    investigationInterface: InvestigationInterface,
}

export class InvestigationMarkerDisplay extends React.Component<InvestigationMarkerDisplayProps> {
    constructor(props: InvestigationMarkerDisplayProps) {
        super(props)

        this.handleSelectClick = this.handleSelectClick.bind(this);
        this.getPinImg = this.getPinImg.bind(this);
    }

    handleSelectClick(event: React.MouseEvent<HTMLElement>) {
        let marker = event.target as any;
        this.props.investigationInterface.select(marker.options.data);
    }

    getPinImg(inv: Investigation) {
        var img = pin_generic_gray_logo
        if((('type' in inv) && inv.type === messages.Encounter.EncounterType.RAD) || !('type' in inv)){
            if (inv.isotopes.length > 0) {
                if (inv.isotopes[0].category.toUpperCase() === 'MEDICAL') {
                    img = pin_medical_logo;
                }
                if (inv.isotopes[0].category.toUpperCase() === 'INDUSTRIAL') {
                    img = pin_industrial_logo;
                }
                if (inv.isotopes[0].category.toUpperCase() === 'RDD') {
                    img = pin_industrial_logo;
                }
                if (inv.isotopes[0].category.toUpperCase() === 'SNM') {
                    img = pin_nuclear_logo;
                }
                if (inv.isotopes[0].category.toUpperCase() === 'NUCLEAR') {
                    img = pin_nuclear_logo;
                }
                if (inv.isotopes[0].category.toUpperCase() === 'NORM') {
                    img = pin_norm_logo;
                }
            } else {
                img = pin_generic_gray_logo;
            }
        } else if (('type' in inv) && inv.type === messages.Encounter.EncounterType.CHEM){
            img = pin_chemical_logo;
        } else if (('type' in inv) && inv.type === messages.Encounter.EncounterType.BIO){
            img = pin_biological_logo;
        } else {
            img = pin_generic_gray_logo;
        }
        return (img);
    }

    render() {
        //console.log('Marker Render @'+Date.now());
        //Investigation Markers
        let markers: JSX.Element[] = [];
        this.props.investigations.forEach((inv) => {
            const key = uuidToString(inv.id);

            let opacity = '0.8';
            if (this.props.selectedInvestigation && inv.id !== this.props.selectedInvestigation.id) {
                opacity = '0.2';
            }

            let visibility_active_badge = 'visible';
            if (inv.finalized) {
                visibility_active_badge = 'hidden';
            }

            const markerIcon = L.divIcon({
                className: 'leaflet-div-pin-icon-clear',
                html:
                    '<div id="inv_' + key + '">' +
                    '<img src="' + pin_shadown_logo + '" style="position:absolute; opacity:' + opacity + '; transition: all 0.5s; left:3px; bottom:-1px"></div>' +
                    '<img src="' + this.getPinImg(inv) +
                    '" style="position:relative; opacity:' + opacity + '; transition: all 0.5s" width="32" height="48">' +
                    '<img class="map-pin-active-badge" src="' + active_badge_logo +
                    '" style="visibility:' + visibility_active_badge +
                    ';position:absolute; opacity:' + opacity + '; transition: all 0.5s; left: 6px; bottom:48px" width="20" height="20">' +
                    '</div>',
                iconSize: [32, 48],
                iconAnchor: [15, 47],
            });

            if (inv.vehicle_location !== null) {
                //console.log(inv.vehicle_location)
                var marker =
                    <Marker position={[inv.vehicle_location.latitude, inv.vehicle_location.longitude]}
                        icon={markerIcon}
                        data={inv}
                        selected={this.props.selectedInvestigation && inv.id === this.props.selectedInvestigation.id}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => this.handleSelectClick(event)}
                        key={key}>
                    </Marker>
                markers.push(marker);
            }
        });

        return (
            markers
        )
    }
}
const MemoizedInvestigationMarkerDisplay = React.memo(InvestigationMarkerDisplay,markersAreEqual);

function markersAreEqual(prevProps:InvestigationMarkerDisplayProps,nextProps:InvestigationMarkerDisplayProps){
        let flag = true;
        if(prevProps.investigations.length > 0){
            if(prevProps.investigations[0] !== nextProps.investigations[0])
                flag = false;
            else if(prevProps.investigations.length !== nextProps.investigations.length){
                flag = false;
            } else {
                let i=0;
                let contiunue_flag = true;
                while (contiunue_flag && i<prevProps.investigations.length){
                    if(prevProps.investigations[i].finalized !== nextProps.investigations[i].finalized){
                        flag = false;
                        contiunue_flag = false;
                    }
                    i++;
                }
            }
        } else {
            flag = false;
        }
        if(prevProps.selectedInvestigation?.id !== nextProps.selectedInvestigation?.id)
            flag = false;

        return (flag);
}

/******************************************************************************/

export interface CoralSourceMarkerDisplayProps {
    selectedInvestigation?: Investigation,
    coralInterface?: CoralInterface,
}

export class CoralSourceMarkerDisplay extends React.Component<CoralSourceMarkerDisplayProps> {
    openPopupRef:React.RefObject<any>|undefined;

    constructor(props: CoralSourceMarkerDisplayProps) {

        super(props)
        this.openPopupRef = undefined;
        this.handleSelectClick = this.handleSelectClick.bind(this);
    }

    componentDidMount(){
        this.checkToOpenPopup();
    }
    componentDidUpdate(prevProps: CoralSourceMarkerDisplayProps ){
        this.checkToOpenPopup();
    }

    checkToOpenPopup(){
        if(this.props.selectedInvestigation?.id !== undefined){
            if(this.openPopupRef !== undefined){
                if(this.openPopupRef.current !== null){
                    this.openPopupRef.current.leafletElement.openPopup();
                }
            }
        }
    }

    handleSelectClick(event: React.MouseEvent<HTMLElement>) {
        console.log('CoralSourceMarker click')
        let marker = event.target as any;
        if(this.props.coralInterface !== undefined){
            const thisCoralInterface = this.props.coralInterface;
            const thisSelectedInvestigation = this.props.selectedInvestigation;
            const thisCoralResults = thisCoralInterface.coralResults;
            if(thisCoralResults !== undefined && Object.keys(thisCoralResults).length > 0 && thisSelectedInvestigation !== undefined){
                const thisCoralResult = thisCoralResults[uuidToString(thisSelectedInvestigation.id)];
                const thisCoralResultSources = thisCoralResult.sources.sort((a, b) => {return a.rank - b.rank});
                const thisCoralResultSelectedSource = thisCoralInterface.selectedSource;
                thisCoralResultSources.forEach((data, idx) => {
                    let selected_flag = false;
                    selected_flag = data.name.includes(marker.options.data.name);
                    if(selected_flag){
                        let selectedSources:{[key:string]:number} = {};
                        console.log('new selected marker ' + marker.options.data.name)
                        if(thisCoralResultSelectedSource !== undefined){
                            console.log(Object.keys(thisCoralResultSelectedSource)[0])

                            if(Object.keys(thisCoralResultSelectedSource)[0] !== marker.options.data.name){
                                console.log('new selection is different than selected')
                                selectedSources[data.name] = data.image.time;
                            }
                        } else {
                            //No previous selection. Add this selection
                            selectedSources[data.name] = data.image.time;
                        }
                        thisCoralInterface.updateSelectedSource(selectedSources);
                    }
                });
            }
        }
    }

    render() {
        //console.log('Coral Source Marker Render @'+Date.now());
        //Coral Source Markers
        let markers: JSX.Element[] = [];

        if(this.props.coralInterface?.coralResults !== undefined && this.props.selectedInvestigation !== undefined){

            //N.B. Only display coral results when in replay mode and the selected investigation matches the replay investigation
            if(this.props.coralInterface?.replayInvestigation?.id !== undefined &&
                uuidToString(this.props.coralInterface.replayInvestigation.id) === uuidToString(this.props.selectedInvestigation.id)) {

                const thisCoralResult = this.props.coralInterface.coralResults[uuidToString(this.props.selectedInvestigation.id)]
                let thisCoralSources;

                var selectedCoralSource:string|undefined;
                if(this.props.coralInterface?.selectedSource !== undefined){
                    selectedCoralSource = Object.keys(this.props.coralInterface.selectedSource)[0];
                }
                if(thisCoralResult !== undefined){
                    thisCoralSources = thisCoralResult.sources;
                    this.openPopupRef = undefined;
                    thisCoralSources.forEach((source) => {
                        const key = source.name;

                        let opacity = '0.8';
                        if (selectedCoralSource !== undefined && source.name !== selectedCoralSource) {
                            opacity = '0.2';
                        }
                        const markerIcon = L.divIcon({
                            className: 'leaflet-div-pin-icon-clear',
                            html:
                                '<div id="coral_' + key + '">' +
                                '<img src="' + pin_shadown_logo + '" style="position:absolute; opacity:' + opacity + '; transition: all 0.5s; left:3px; bottom:-1px"></div>' +
                                '<img src="' + pin_generic_gray_logo +'" style="position:relative; opacity:' + opacity + '; transition: all 0.5s" width="32" height="48">' +
                                '<div style="position:absolute; bottom:24px; left:13px; font-weight:700; text-align:center; opacity:' + opacity +'">'+source.rank+'</div>'+
                                '</div>',
                            iconSize: [32, 48],
                            iconAnchor: [15, 47],
                        });
                        if (source.lat_long !== null) {
                            //console.log(inv.vehicle_location)
                            let popupDisplay;
                            let thisMarkerRef;
                            if(selectedCoralSource !== undefined && source.name === selectedCoralSource){
                                this.openPopupRef = React.createRef();
                                let categories='';
                                source.categories.forEach((cat,idx) => {
                                    if(idx > 0)
                                        categories+=', ';
                                    categories+=cat;
                                });
                                popupDisplay =
                                    <Popup closeButton={false} closeOnClick={false} offset={L.point([2,-40])} autoPanPadding={L.point([50,50])} style={{opacity:0.9}}>
                                        <div style={{ display: 'flex', flexDirection: 'column'}}>
                                            <div style={{}}>{source.name}</div>
                                            <div style={{}}>{source.address}</div>
                                            <div style={{}}>{source.phone}</div>
                                            <div style={{}}>{categories}</div>
                                            <div style={{}}>{source.description}</div>
                                        </div>
                                    </Popup>
                                thisMarkerRef = this.openPopupRef;
                            }
                            var marker =
                                <Marker position={[source.lat_long[0], source.lat_long[1]]}
                                    icon={markerIcon}
                                    data={source}
                                    selected={selectedCoralSource !== undefined && source.name !== selectedCoralSource}
                                    onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => this.handleSelectClick(event)}
                                    ref={thisMarkerRef}
                                    key={key}>
                                    {popupDisplay}
                                </Marker>
                            markers.push(marker);
                        }
                    });
                }
            }
        }
        return (
            markers
        )
    }
}
const MemoizedCoralSourceMarkerDisplay = React.memo(CoralSourceMarkerDisplay,coralSourceMarkersAreEqual);

function coralSourceMarkersAreEqual(prevProps:CoralSourceMarkerDisplayProps,nextProps:CoralSourceMarkerDisplayProps){
        let flag;
        if(prevProps.coralInterface?.selectedSource !== nextProps.coralInterface?.selectedSource){
            flag = false;
        } else if (prevProps.coralInterface?.coralResults !== nextProps.coralInterface?.coralResults){
            flag = false;
        } else if (prevProps.selectedInvestigation?.id !== nextProps.selectedInvestigation?.id){
            flag = false;
        } else {
            flag = true;
        }
        return (flag);
}

/******************************************************************************/

export interface DoseTrailDisplayProps {
    positions: messages.Core.GPSReading[],
    radiationData: messages.Rad.RadiationReading[],
    plotSince: number
}

export class DoseTrailDisplay extends React.Component<DoseTrailDisplayProps> {
    dose_min: number = 1.e8;
    dose_max: number = 0.0;
    dose_ave: number = 0;
    dose_range: number = 0;
    dose = 0.0;
    constructor(props: DoseTrailDisplayProps) {
        super(props)

        this.doseColorMap = this.doseColorMap.bind(this);
        this.doseRateFeatureStyle = this.doseRateFeatureStyle.bind(this);
    }
    //GEOJSON STYLE
    doseColorMap(counts: number, ave_counts: number, min_counts: number, count_range: number) {
        if (counts === 0) {
            return 'rgb(0,0,0)';
        } else if (counts < ave_counts) {
            return 'rgb(0,0,' +
                Math.floor((counts - min_counts) / count_range * 255) + ')';
        } else {
            return 'rgb(0,' +
                Math.floor((counts - ave_counts) / count_range * 255) +
                ',255)';
        }
    }

    doseRateFeatureStyle = (feature: any) => {
        return {
            color:
                this.doseColorMap(feature.properties.counts, this.dose_ave, this.dose_min, this.dose_range),
            opacity: 1,
            weight: 5
        };
    };

    render() {
        //Dose Lines
        //console.log('Dose Trail Render @'+Date.now())
        let ndoses = 0, dose_sum = 0.0;
        this.dose_min = 1.e8;
        this.dose_max = 0.0;
        this.dose_ave = 0;
        this.dose_range = 0;
        this.dose = 0.0;
        let dose_lines: geojson.Feature[] = [];
        //var poly_lines: L.LatLngExpression[] = [];
        var prev_position: number[] | null = null;
        if(this.props.positions !== undefined && this.props.positions !== null){
            this.props.positions.forEach((gps_reading) => {
                if(gps_reading !== undefined && gps_reading !== null){
                    let time = gps_reading.time;
                    let position = [gps_reading.lon, gps_reading.lat];
                    let dose = 0.0;
                    if( time > this.props.plotSince){
                        if (this.props.radiationData !== null) {
                            if (this.props.radiationData.length > 0) {
                                let match_idx: number | null = null;
                                let prev_diff: number | null = null;
                                this.props.radiationData.forEach((rad_reading, idx) => {
                                    let this_diff = rad_reading.time - gps_reading.time;
                                    if (prev_diff != null) {
                                        if (this_diff <= 0) {
                                            //Difference Negative=>GPS time is newer
                                            if (this_diff > prev_diff) {
                                                //current is newer than prev without being newer than GPS
                                                if (Math.abs(this_diff) < 10000) {
                                                    match_idx = idx;
                                                }
                                            }
                                        }
                                    } else {
                                        prev_diff = this_diff;
                                    }
                                });

                                if (match_idx != null) {
                                    let sum_count = 0;
                                    let sum_dose = 0;
                                    this.props.radiationData[match_idx].detectors.forEach((det) => {
                                        det.spectrum.forEach((count) => {
                                            sum_count += count;
                                        });
                                        sum_dose += det.dose_rate;
                                    })

                                    dose = sum_count;
                                    dose_sum += dose;
                                    this.dose_min = Math.min(this.dose_min, dose);
                                    this.dose_max = Math.max(this.dose_max, dose);
                                    ndoses += 1;
                                } else {
                                    dose = 0;
                                }
                            }
                        }

                        if (prev_position !== null) {
                            let new_feature: geojson.Feature = {
                                "type": "Feature",
                                "properties": { "time": time, "counts": dose },
                                "geometry": {
                                    "type": "LineString",
                                    "coordinates": [prev_position, position]
                                }
                            };
                            dose_lines.push(new_feature);
                        }
                        prev_position = position;
                    }
                }
            });
        }
        if (ndoses > 0) {
            this.dose_ave = dose_sum / ndoses;
            this.dose_range = Math.max(this.dose_max - this.dose_ave, this.dose_ave - this.dose_min);
        } else {
            this.dose_ave = 0;
            this.dose_range = 0;
        }
        var key = new Date().getTime(); //Change in key triggers the GeoJSON redraw

        return (
            <GeoJSON key={key} data={dose_lines} style={this.doseRateFeatureStyle} />
        )

    }

}
const MemoizedDoseTrailDisplay = React.memo(DoseTrailDisplay,trailsAreEqual);

function trailsAreEqual(prevProps:DoseTrailDisplayProps,nextProps:DoseTrailDisplayProps){

        const flag = prevProps.positions === nextProps.positions &&
        prevProps.radiationData === nextProps.radiationData &&
        prevProps.plotSince === nextProps.plotSince

        return (flag);
}
